import { useState, useEffect } from "react";
import { EyeOutlined, EyeInvisibleOutlined, CheckCircleOutlined } from "@ant-design/icons";
import UserProfileAPI from "../services/UserProfileAPI";
import type { Route } from "./+types/login";
import { NetworkResponseDisplay, type NetworkResponse } from "~/components/NetworkResponse";

export function meta({}: Route.MetaArgs) {
  return [
    { title: "Login - Atila" },
    { name: "description", content: "Login to your Atila account" },
  ];
}

export default function Login() {
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [networkResponse, setNetworkResponse] = useState<NetworkResponse>({title: "", type: null});
  const [error, setError] = useState("");
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [isClient, setIsClient] = useState(false);

  // Fix for handling localStorage in SSR
  useEffect(() => {
    setIsClient(true);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (error) setError("");
    // Reset success state if user tries to change credentials after successful login
    if (loginSuccess) setLoginSuccess(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!credentials.username || !credentials.password) {
      setError("Please enter both username/email and password");
      return;
    }
    
    setNetworkResponse({title: "Logging In", type: "loading"});
    
    try {
      // Use the UserProfileAPI to handle login
      const response = await UserProfileAPI.login(credentials);
      console.log("Login response:", response);
      const data = response.data;
      
      // Store user data in localStorage (only in client)
      if (isClient) {
        localStorage.setItem("atilaToken", data.token);
        localStorage.setItem("atilaUser", JSON.stringify({
          id: data.id,
          username: data.username,
          user_profile: data.user_profile
        }));
        UserProfileAPI.authenticateRequests();
      }
      
      // Show success message
      setNetworkResponse({title: "Login successful", type: "success"});
      // Redirect based on account type
      window.location.href = data.user_profile.account_type === "mentor" 
          ? `/profile/${data.username}` 
          : "/mentorship";
      
    } catch (err: any) {
      // Handle Axios error
      const errorMessage = 
        err.response?.data?.message || 
        err.response?.data?.error || 
        err.message || 
        "Invalid Credentials";
      setNetworkResponse({title: errorMessage, type: "error"});
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-gray-50 p-4 dark:bg-gray-900">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Login to Atila</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Enter your credentials to access your account
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="rounded-lg bg-white p-8 shadow-md dark:bg-gray-800">
            
            <NetworkResponseDisplay response={networkResponse} />
            
            <div className="mb-4">
              <label 
                htmlFor="username" 
                className="mb-2 block text-sm font-medium text-gray-900 dark:text-gray-200"
              >
                Username or Email
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={credentials.username}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-400"
                placeholder="Enter your username or email"
              />
            </div>
            
            <div className="mb-6">
              <label 
                htmlFor="password" 
                className="mb-2 block text-sm font-medium text-gray-900 dark:text-gray-200"
              >
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={credentials.password}
                  onChange={handleChange}
                  className="w-full rounded-md border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-400"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeInvisibleOutlined className="text-lg" />
                  ) : (
                    <EyeOutlined className="text-lg" />
                  )}
                </button>
              </div>
            </div>
            
            <button
              type="submit"
              disabled={networkResponse.type === "loading" || networkResponse.type === "success"}
              className="w-full rounded-md bg-blue-600 py-2"
              style={{color: "white", cursor: networkResponse.type === "loading" ? "not-allowed" : "pointer"}}
            >
              {networkResponse.type === "loading" ? "Logging in..." : "Login"}
            </button>
            
            <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
              Don't have an account?{" "}
              <a href="/register" className="text-blue-600 hover:underline dark:text-blue-400">
                Sign up
              </a>
            </div>
          </form>
      </div>
    </main>
  );
}